import { BoltIcon, ClockIcon, StopCircleIcon } from '@heroicons/react/24/outline'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { useState } from 'react'
import { SyncLoader } from 'react-spinners'
import {
    useAccountMonitoringQuery,
    useCreateMonitorProfileScheduleMutation,
    useCreateScrapedHistoricFetchMutation,
    useRunScheduledWorkNowMutation,
    useStopScheduledWorkMutation,
    MonitorMode
} from '../../api/client'
import { toastError, toastSuccess } from '../Notification'
import { getPlatformDisplayName } from '../../util/types'
import CopyMonitoringToTeam from './CopyMonitoringToTeam'
import HistoricFetchModal from './HistoricFetchModal'
import MonitorScheduleForm, { MonitorScheduleValues } from './MonitorScheduleForm'
import MonitorTargets from './MonitorTargets'
import { platformFor } from './platforms'
import StopScheduledWorkModal from './StopScheduledWorkModal'
import { isOneOffFetchTag } from './tags'

type Props = {
    /** The account's `socialMediaServiceShortName`, in whatever case it holds it. */
    platform: string
    /** The account's handle: its serviceAccountUsername. */
    serviceAccountUsername: string
    /** Arwen's own account id, for copying the schedule across a team. */
    accountId?: number
    /** Whether Arwen holds authorisation for the account. */
    hasAuthorisation?: boolean
}

/** How often the section refreshes while you are watching a run. */
const POLL_INTERVAL_MS = 10000

const statusChipClasses = (status: string) => {
    switch (status) {
        case 'Running':
            return 'text-info bg-info-soft'
        case 'Pending':
            return 'text-safe-700 bg-safe-100'
        case 'Deferred':
            return 'text-suspect-700 bg-suspect-100'
        case 'Failed':
        case 'Panicked':
            return 'text-severe-700 bg-severe-100'
        default:
            return 'text-ink-700 bg-ink-100'
    }
}

/**
 * Why a run exists.
 */
const reasonText = (tag: string, scheduledBy: string) => {
    const isHistoric = isOneOffFetchTag(tag)
    if (scheduledBy.startsWith('backoff:')) {
        const after = scheduledBy.slice('backoff:'.length)
        return isHistoric ? `Historic fetch, retried after ${after}` : `Retry after ${after}`
    }
    if (isHistoric) return 'Historic fetch'
    if (scheduledBy.startsWith('cron:') || scheduledBy.startsWith('every:')) {
        return 'Scheduled run'
    }
    if (scheduledBy === 'hint') return 'Time chosen by the job'
    if (scheduledBy === 'defer') return 'Put off by a rate limit'
    if (scheduledBy === 'skip') return 'Replaced a cancelled run'
    if (scheduledBy === 'user') return 'Asked for by hand'
    return scheduledBy
}

/**
 * Comment monitoring for one account, on the account's own settings page.
 *
 * Shows the schedule and, beneath it, every run for this account: the daily
 * ones, their retries, and any historic scrapes.
 */
function AccountMonitoringCard({
    platform,
    serviceAccountUsername,
    accountId,
    hasAuthorisation = false
}: Props) {
    const [showRuns, setShowRuns] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [stopTag, setStopTag] = useState<string | null>(null)
    const [historicFetchTag, setHistoricFetchTag] = useState<string | null>(null)

    const config = platformFor(platform)
    const wireValue = config?.wireValue ?? platform.toLowerCase()

    const { data, loading, error, refetch } = useAccountMonitoringQuery({
        variables: { platform: wireValue, serviceAccountUsername },
        fetchPolicy: 'cache-and-network',
        pollInterval: POLL_INTERVAL_MS
    })

    const [createSchedule, { loading: creating }] = useCreateMonitorProfileScheduleMutation()
    const [stopWork, { loading: stopping }] = useStopScheduledWorkMutation()
    const [runNow, { loading: running }] = useRunScheduledWorkNowMutation()
    const [createScrapedHistoricFetch, { loading: fetchingHistory }] =
        useCreateScrapedHistoricFetchMutation()
    const busy = creating || stopping || running || fetchingHistory

    const monitor = data?.accountMonitoring?.monitor
    const settings = data?.accountMonitoring?.settings
    const runs = data?.accountMonitoring?.runs ?? []
    const monitorRunCount = monitor ? runs.filter((run) => run.tag === monitor.tag).length : 0
    // A stopped monitor's newest row is terminal, and get_work returns the
    // newest row whatever its status — so a monitor here is history, not a
    // schedule, unless it says it is live. Its tag is free again, which is
    // what lets the form below start monitoring afresh.
    const live = monitor?.live ?? false

    const handleCreate = async (values: MonitorScheduleValues) => {
        try {
            const result = await createSchedule({
                variables: {
                    input: { platform: wireValue, serviceAccountUsername, ...values }
                }
            })
            toastSuccess(
                `Monitoring set up as ${result.data?.createMonitorProfileSchedule.tag}. The first run happens at the next scheduled time.`
            )
            setShowForm(false)
            refetch()
        } catch (err: any) {
            toastError(err.message)
        }
    }

    /**
     * The lifecycle mutations all return {changed, message}: `message` is ready
     * for a toast and `changed` says whether anything actually happened, so
     * "already stopped" does not pretend to be a fresh stop.
     */
    const runAction = async (
        action: () => Promise<{ changed: boolean; message: string } | undefined>
    ) => {
        try {
            const result = await action()
            if (result) {
                toastSuccess(result.message)
                if (result.changed) refetch()
            }
        } catch (err: any) {
            toastError(err.message)
        }
    }

    if (loading && !data) {
        return (
            <div className="flex justify-center py-4">
                <SyncLoader color="#dc3b32" size="6px" />
            </div>
        )
    }

    if (error) {
        return <p className="text-sm text-severe-600">Could not load monitoring: {error.message}</p>
    }

    return (
        <div className="space-y-4">
            {monitor && live ? (
                <>
                    {hasAuthorisation ? (
                        <div className="rounded-md bg-severe-50 p-3 text-sm text-severe-800">
                            <span className="font-medium">
                                This profile is authorised and is also being scraped.
                            </span>{' '}
                            Its comments are arriving twice, by two routes, under different ids.
                            Scraping an authorised profile is no longer allowed, so this schedule
                            predates that — stop it unless you know why it is here.
                        </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-3">
                        <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusChipClasses(
                                monitor.status
                            )}`}
                        >
                            {monitor.status}
                        </span>
                        {/* The status above will be "Pending" even after a failure, because it's recurring.
                            So to make the fact of the failure visible we can show chip to indicate failure too.*/}
                        {monitor.needsAttention ? (
                            <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-severe-700 bg-severe-100"
                                title={monitor.lastRunLog ?? undefined}
                            >
                                Last run failed
                                {monitor.consecutiveFailures > 1
                                    ? ` ×${monitor.consecutiveFailures}`
                                    : ''}
                            </span>
                        ) : null}
                        <span className="text-sm text-ink-900">
                            {monitor.scheduleEnglish ?? monitor.cron ?? 'one-off'}
                        </span>
                        {monitor.nextRunAt ? (
                            <span className="text-sm text-ink-500">
                                next{' '}
                                {formatDistanceToNow(parseISO(monitor.nextRunAt), {
                                    addSuffix: true
                                })}
                            </span>
                        ) : null}

                        <div className="ml-auto flex items-center gap-2">
                            <button
                                onClick={() =>
                                    runAction(async () => {
                                        const r = await runNow({ variables: { tag: monitor.tag } })
                                        return r.data?.runScheduledWorkNow
                                    })
                                }
                                disabled={busy}
                                className="inline-flex items-center gap-1 text-sm text-arwen-600 hover:text-arwen-700 disabled:opacity-50"
                                title="Bring the next run forward to now"
                            >
                                <BoltIcon className="h-4 w-4" />
                                Run now
                            </button>
                            <button
                                onClick={() => setHistoricFetchTag(monitor.tag)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 text-sm text-arwen-600 hover:text-arwen-700 disabled:opacity-50"
                                title="Collect comments from before monitoring started"
                            >
                                <ClockIcon className="h-4 w-4" />
                                Historic fetch…
                            </button>
                            <button
                                onClick={() => setStopTag(monitor.tag)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 text-sm text-severe-600 hover:text-severe-800 disabled:opacity-50"
                                title="Stop monitoring this account"
                            >
                                <StopCircleIcon className="h-4 w-4" />
                                Stop
                            </button>
                        </div>
                    </div>
                </>
            ) : config && hasAuthorisation ? (
                <div className="space-y-3">
                    <div className="rounded-md bg-ink-50 p-3 text-sm text-ink-700">
                        <p>
                            <span className="font-medium">Not available for this profile.</span>{' '}
                            Arwen is authorised for it, so its comments already arrive through{' '}
                            {config.label}&apos;s own API.
                        </p>
                        <p className="mt-2">
                            Scraping it as well would collect the same comments a second time, under
                            different ids — so moderation actions might not apply to them, and they
                            would sit alongside the authorised ones. The two routes are kept
                            separate deliberately.
                        </p>
                    </div>
                    <MonitorScheduleForm
                        platform={config}
                        hasAccount={true}
                        submitLabel="Start monitoring"
                        submitting={false}
                        disabled={true}
                        onSubmit={() => undefined}
                    />
                </div>
            ) : config ? (
                <div className="space-y-3">
                    {monitor ? (
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-ink-700 bg-ink-100">
                                Ended
                            </span>
                            <span className="text-sm text-ink-600">
                                {/* The runs list spans the whole account, historic
                                    fetches included, so count only the monitor's own. */}
                                Monitoring stopped after {monitorRunCount}{' '}
                                {monitorRunCount === 1 ? 'run' : 'runs'}. Set it up again below.
                            </span>
                            {/* Still offered: the backend takes a historic
                                fetch against a stopped monitor on purpose. */}
                            <button
                                onClick={() => setHistoricFetchTag(monitor.tag)}
                                disabled={busy}
                                className="ml-auto inline-flex items-center gap-1 text-sm text-arwen-600 hover:text-arwen-700 disabled:opacity-50"
                                title="Collect comments from before monitoring started"
                            >
                                <ClockIcon className="h-4 w-4" />
                                Historic fetch…
                            </button>
                        </div>
                    ) : (
                        <p className="text-sm text-ink-600">
                            Not monitored. Arwen can collect this account&apos;s comments by
                            scraping, on a schedule you choose.
                        </p>
                    )}
                    {showForm ? (
                        <MonitorScheduleForm
                            platform={config}
                            hasAccount={true}
                            submitLabel="Start monitoring"
                            submitting={creating}
                            onSubmit={handleCreate}
                            onCancel={() => setShowForm(false)}
                        />
                    ) : (
                        <button
                            onClick={() => setShowForm(true)}
                            disabled={busy}
                            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-arwen-600 hover:bg-arwen-700 disabled:opacity-50"
                        >
                            {monitor ? 'Start monitoring again…' : 'Set up monitoring…'}
                        </button>
                    )}
                    <p className="text-xs text-ink-500">
                        To collect comments from before today, start monitoring and then use
                        &apos;Historic fetch…&apos;.
                    </p>
                </div>
            ) : hasAuthorisation ? null : (
                /* Only show for unauthorised accounts -- there's no point in
                   scraping authorised accounts because we can fetch with API.
                 */
                <p className="text-sm text-ink-600">
                    Not monitored. Scheduled scraping does not cover{' '}
                    {getPlatformDisplayName(platform)} accounts.
                </p>
            )}

            {/* A posts-mode monitor's own list, and copying the schedule
                across a team: both need the monitor's settings, so both live
                behind one. */}
            {monitor && live && settings && config ? (
                <div className="space-y-4 border-t border-ink-200 pt-3">
                    {settings.mode === MonitorMode.Posts ? (
                        <MonitorTargets
                            monitorTag={monitor.tag}
                            platform={config}
                            targets={settings.targets}
                            onChanged={refetch}
                        />
                    ) : null}
                    {accountId !== undefined && monitor.cron ? (
                        <CopyMonitoringToTeam
                            sourceAccountId={accountId}
                            template={{
                                mode: settings.mode,
                                windowDays: settings.windowDays,
                                since: settings.since,
                                targets: settings.targets,
                                scrapeProfile: settings.scrapeProfile,
                                cron: monitor.cron
                            }}
                        />
                    ) : null}
                </div>
            ) : null}

            {/* The runs */}
            {runs.length > 0 ? (
                <div>
                    <button
                        onClick={() => setShowRuns((shown) => !shown)}
                        className="text-sm text-arwen-600 hover:text-arwen-700"
                    >
                        {showRuns ? 'Hide' : 'Show'} {runs.length} run
                        {runs.length === 1 ? '' : 's'}
                    </button>
                    {showRuns ? (
                        <div className="mt-2 overflow-x-auto">
                            <table className="min-w-full divide-y divide-ink-200">
                                <thead className="bg-ink-50">
                                    <tr>
                                        {['Status', 'Why', 'When', 'What happened'].map(
                                            (heading) => (
                                                <th
                                                    key={heading}
                                                    className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase tracking-wider"
                                                >
                                                    {heading}
                                                </th>
                                            )
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-ink-200">
                                    {runs.map((run) => (
                                        <tr key={run.rowId}>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusChipClasses(
                                                        run.status
                                                    )}`}
                                                >
                                                    {run.status}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-sm text-ink-900">
                                                {reasonText(run.tag, run.scheduledBy)}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-500">
                                                {run.startedAt
                                                    ? format(
                                                          parseISO(run.startedAt),
                                                          'dd/MM/yyyy HH:mm'
                                                      )
                                                    : 'not yet'}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-ink-700 break-words">
                                                {run.log ?? <span className="text-ink-400">—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>
            ) : null}

            <HistoricFetchModal
                monitorTag={historicFetchTag}
                loading={fetchingHistory}
                onClose={() => setHistoricFetchTag(null)}
                onConfirm={async (since, scrapeProfile) => {
                    if (!historicFetchTag) return
                    try {
                        const result = await createScrapedHistoricFetch({
                            variables: { monitorTag: historicFetchTag, since, scrapeProfile }
                        })
                        const outcome = result.data?.createScrapedHistoricFetch
                        if (outcome) {
                            toastSuccess(outcome.message)
                            refetch()
                        }
                    } catch (err: any) {
                        toastError(err.message)
                    }
                    setHistoricFetchTag(null)
                }}
            />

            <StopScheduledWorkModal
                open={stopTag !== null}
                tag={stopTag ?? ''}
                loading={stopping}
                onClose={() => setStopTag(null)}
                onConfirm={async () => {
                    if (!stopTag) return
                    await runAction(async () => {
                        const r = await stopWork({ variables: { tag: stopTag } })
                        return r.data?.stopScheduledWork
                    })
                    setStopTag(null)
                }}
            />
        </div>
    )
}

export default AccountMonitoringCard
